import React, { useEffect, useRef } from 'react';
import { Trash2, Hash, ArrowUpDown } from 'lucide-react';

interface MarkerContextMenuProps {
  visible: boolean;
  position: { x: number; y: number };
  selectedCount: number;
  selectedMarkerId?: number;
  onDelete: () => void;
  onChangeSequence?: () => void;
  onAdjustSequence?: () => void;
  onClose: () => void;
}

export const MarkerContextMenu: React.FC<MarkerContextMenuProps> = ({
  visible,
  position,
  selectedCount,
  onDelete,
  onChangeSequence,
  onAdjustSequence,
  onClose,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // Small delay to prevent immediate close from the right-click event
    setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }, 0);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [visible, onClose]);

  if (!visible) return null;

  return (
    <div
      ref={menuRef}
      className="fixed z-[100] bg-popover rounded-lg shadow-lg border border-border py-1 min-w-[200px]"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
    >
      {selectedCount === 1 && onChangeSequence && (
        <button
          className="w-full px-4 py-2 text-left text-sm hover:bg-accent flex items-center gap-2"
          onClick={(e) => {
            e.stopPropagation();
            onChangeSequence();
            onClose();
          }}
        >
          <Hash className="h-4 w-4" />
          Change Sequence
        </button>
      )}
      {selectedCount >= 1 && onAdjustSequence && (
        <button
          className="w-full px-4 py-2 text-left text-sm hover:bg-accent flex items-center gap-2"
          onClick={(e) => {
            e.stopPropagation();
            onAdjustSequence();
            onClose();
          }}
        >
          <ArrowUpDown className="h-4 w-4" />
          Adjust Sequence
        </button>
      )}
      <button
        className="w-full px-4 py-2 text-left text-sm hover:bg-accent flex items-center gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
          onClose();
        }}
      >
        <Trash2 className="h-4 w-4" />
        Delete {selectedCount > 1 ? `${selectedCount} markers` : 'marker'}
      </button>
    </div>
  );
};


